// Browser-only geolocation wrapper for the ZMEX Driver web app.
//
// Wraps `navigator.geolocation` in a Promise-based API that returns a
// consistent shape, and normalises errors into a small enum so the UI can
// distinguish "permission denied" from "GPS timed out" without re-parsing
// browser-specific error messages.

/**
 * Resolve the driver's current position.
 * @param {object} opts – { timeout, enableHighAccuracy, maximumAge }
 * @returns {Promise<{coords: {latitude:number, longitude:number, accuracy?:number}, ts:number, source:"web"}>}
 * @throws  {DriverGeoError} – .code ∈ "PERMISSION_DENIED"|"TIMEOUT"|"UNAVAILABLE"|"UNKNOWN"
 */
export async function getCurrentPosition(opts = {}) {
  const enableHighAccuracy = opts.enableHighAccuracy !== false;
  const timeout = opts.timeout ?? 15000;
  const maximumAge = opts.maximumAge ?? 5000;

  if (!navigator?.geolocation) {
    throw new DriverGeoError("UNAVAILABLE", "This browser doesn't support location.");
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        coords: {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        },
        ts: pos.timestamp,
        source: "web",
      }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          reject(new DriverGeoError("PERMISSION_DENIED", "Location permission denied.", err));
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          reject(new DriverGeoError("UNAVAILABLE", "Location unavailable.", err));
        } else if (err.code === err.TIMEOUT) {
          reject(new DriverGeoError("TIMEOUT", "Timed out getting your location.", err));
        } else {
          reject(new DriverGeoError("UNKNOWN", err.message || "Unknown location error.", err));
        }
      },
      { enableHighAccuracy, timeout, maximumAge },
    );
  });
}

/** Query the current permission state without prompting. */
export async function checkLocationPermission() {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return { state: "unknown" };
  }
  try {
    const p = await navigator.permissions.query({ name: "geolocation" });
    return { state: p.state }; // "granted" | "denied" | "prompt"
  } catch (e) {
    return { state: "unknown", error: e };
  }
}

export class DriverGeoError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = "DriverGeoError";
    this.code = code;
    this.cause = cause;
  }
}
